import type React from 'react';
import { useCallback } from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV } from '@fortawesome/pro-solid-svg-icons/faEllipsisV';

import { FlexRow } from 'ui-components/components/flex/row';
import { FlexColumn } from 'ui-components/components/flex/column';
import { GlobalEventListenerRoot } from 'ui-components/components/global-event-listener/root';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { Gap } from 'ui-components/components/flex/gap';
import { Text } from 'ui-components/components/text';
import { TextType } from 'ui-components/components/text/text-type';

import type { WorkflowStep, WorkflowEntityType } from '../../types';
import { StepEditor } from '../step-editor';
import { ActionStepContent } from './action-step-content';
import { messages } from './messages';
import styles from './styles.module.less';

type ActionStepCardProps = {
  step: WorkflowStep;
  entityType: WorkflowEntityType;
  isSelected: boolean;
  onSelect: (stepId: string | null) => void;
  onDelete: (stepId: string) => void;
  onUpdateStep: (step: WorkflowStep) => void;
};

export const ActionStepCard: React.FC<ActionStepCardProps> = ({ step, entityType, isSelected, onSelect, onDelete, onUpdateStep }) => {
  const intl = useIntl();

  const handleCardClick = useCallback(() => {
    onSelect(isSelected ? null : step.id);
  }, [isSelected, onSelect, step.id]);

  const handleDelete = useCallback(() => {
    onDelete(step.id);
  }, [onDelete, step.id]);

  return (
    <div className={classnames(styles.card, { [styles.selected]: isSelected })} data-step-card>
      <FlexColumn gap={Gap.Space8}>
        <FlexRow className={styles['card-header']} gap={Gap.Space16}>
          <div className={styles.content} onClick={handleCardClick}>
            <ActionStepContent step={step} />
          </div>
          <DropdownMenu>
            <DropdownMenuToggler
              ariaLabel={intl.formatMessage(messages.stepOptionsAriaLabel)}
              className={styles['options-toggler']}
              hideChevron
            >
              <FontAwesomeIcon icon={faEllipsisV} />
            </DropdownMenuToggler>
            {/* TODO: add Duplicate step menu item */}
            {/* TODO: add Move up / Move down menu items */}
            <DropdownMenuItem onClick={handleDelete}>
              <Text textType={TextType.Caption} value={messages.deleteStep} />
            </DropdownMenuItem>
          </DropdownMenu>
        </FlexRow>
        {isSelected && (
          <GlobalEventListenerRoot>
            <StepEditor entityType={entityType} onUpdateStep={onUpdateStep} step={step} />
          </GlobalEventListenerRoot>
        )}
      </FlexColumn>
    </div>
  );
};
